/*
 * Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this
 * software and associated documentation files (the "Software"), to deal in the Software
 * without restriction, including without limitation the rights to use, copy, modify,
 * merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import {
  HttpClient,
  HttpParams,
  HttpParamsOptions,
} from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import { map, switchMap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ConfigParams } from './models/config-params';
import { ActivatedRoute } from '@angular/router';
import Amplify, { Auth } from 'aws-amplify';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { TenantEntitlementService } from './tenant-entitlement.service';

@Injectable({
  providedIn: 'root',
})
export class AuthConfigurationService {
  params$: Observable<ConfigParams>;
  params: ConfigParams;
  tenantName: string;
  private configuredUserPoolId: string | null = null;
  private configuredAppClientId: string | null = null;
  // App chrome stays hidden until JWT custom:tenantId matches GET /tenant/init.
  private sessionAllowedSubject = new BehaviorSubject<boolean>(false);
  readonly sessionAllowed$ = this.sessionAllowedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private tenantEntitlements: TenantEntitlementService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  public setTenantConfig(tenantName: string): Promise<any> {
    const url = `${environment.regApiGatewayUrl}/tenant/init/` + tenantName;
    this.params$ = this.http.get<ConfigParams>(url);
    const setup$ = this.params$.pipe(
      map((val) => {
        // remove trailing slash (/) if present
        val.apiGatewayUrl = val.apiGatewayUrl.replace(/\/$/, '');
        localStorage.setItem('userPoolId', val.userPoolId);
        localStorage.setItem('tenantName', tenantName);
        localStorage.setItem('appClientId', val.appClientId);
        localStorage.setItem('apiGatewayUrl', val.apiGatewayUrl);
        if (!val.tenantId) {
          throw new Error('GET /tenant/init did not return tenantId');
        }
        localStorage.setItem('tenantId', val.tenantId);
        this.tenantEntitlements.storeAllowedActions(val.allowedActions);
        return 'success';
      }),
      catchError((error) => {
        console.log('Error setting tenant config: ', error);
        return throwError(error);
      })
    );

    return setup$.toPromise();
  }

  // Re-fetch GET /tenant/init for the cached tenant so sidebar allowlists pick up plan changes.
  // NavComponent calls this on startup when tenantName is already in localStorage.
  refreshAllowedActionsFromStoredTenant(): Promise<any> {
    const tenantName = (localStorage.getItem('tenantName') || '').trim();
    if (!tenantName) {
      return Promise.resolve();
    }
    return this.setTenantConfig(tenantName).catch((error) => {
      console.error('Error refreshing tenant entitlements:', error);
    });
  }

  configureAmplifyAuth(): boolean {
    try {
      const userPoolId = localStorage.getItem('userPoolId');
      const appClientId = localStorage.getItem('appClientId');

      if (!userPoolId || !appClientId) {
        return false;
      }
      // Same pool is already configured; a second Amplify.configure() races a fast login.
      if (
        this.configuredUserPoolId === userPoolId &&
        this.configuredAppClientId === appClientId
      ) {
        return true;
      }
      const region = userPoolId?.split('_')[0];
      const awsmobile = {
        aws_project_region: region,
        aws_cognito_region: region,
        aws_user_pools_id: userPoolId,
        aws_user_pools_web_client_id: appClientId,
      };

      Amplify.configure(awsmobile);
      this.configuredUserPoolId = userPoolId;
      this.configuredAppClientId = appClientId;
      return true;
    } catch (err) {
      console.error('Unable to initialize amplify auth.', err);
      return false;
    }
  }

  cleanLocalStorage() {
    // Clear tenant-specific storage
    localStorage.removeItem('tenantName');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('userPoolId');
    localStorage.removeItem('appClientId');
    localStorage.removeItem('apiGatewayUrl');
    this.tenantEntitlements.clearAllowedActions();
    this.configuredUserPoolId = null;
    this.configuredAppClientId = null;
    this.sessionAllowedSubject.next(false);

    // Clear all Cognito-related localStorage keys
    // This ensures complete cleanup of user credentials
    Object.keys(localStorage)
      .filter(key => key.includes('CognitoIdentityServiceProvider'))
      .forEach(key => localStorage.removeItem(key));
  }

  // Amplify Authenticator shows the app before Hub currentSession is ready; keep chrome hidden until JWT matches.
  // VerifyTenantSessionComponent calls this with the authenticated-slot user.
  async allowAuthenticatedSessionIfTenantMatches(hubUser?: unknown): Promise<void> {
    this.sessionAllowedSubject.next(false);
    const selected = (localStorage.getItem('tenantId') || '').trim();
    const jwtTenantId = await this.readJwtTenantId(hubUser);
    if (selected && jwtTenantId === selected) {
      this.sessionAllowedSubject.next(true);
      return;
    }
    try {
      await Auth.signOut();
    } catch (err) {
      console.error('Error signing out mismatched tenant session:', err);
    }
    Object.keys(localStorage)
      .filter(key => key.includes('CognitoIdentityServiceProvider'))
      .forEach(key => localStorage.removeItem(key));
    this.translate.get('ERROR.TENANT_SESSION_MISMATCH').subscribe((msg) => {
      this.snackBar.open(msg, '', { duration: 6000 });
    });
  }

  // Prefer the Amplify slot user, then retry currentSession after a fast login.
  // allowAuthenticatedSessionIfTenantMatches calls this.
  private async readJwtTenantId(hubUser: unknown): Promise<unknown> {
    const fromUser = this.jwtTenantIdFromUser(hubUser);
    if (fromUser !== undefined && fromUser !== '') {
      return fromUser;
    }
    for (let attempt = 0; attempt < 8; attempt++) {
      try {
        const sesh = await Auth.currentSession();
        if (sesh && typeof sesh.isValid === 'function' && sesh.isValid()) {
          return sesh.getIdToken().payload['custom:tenantId'];
        }
      } catch {
        // Session not ready yet after a fast login.
      }
      if (attempt < 7) {
        await new Promise((resolve) => setTimeout(resolve, 50 * (attempt + 1)));
      }
    }
    return undefined;
  }

  // Read custom:tenantId from an Amplify Cognito user object.
  // readJwtTenantId calls this.
  private jwtTenantIdFromUser(hubUser: unknown): unknown {
    if (!hubUser || typeof hubUser !== 'object') {
      return undefined;
    }
    const user = hubUser as Record<string, unknown>;
    const session = (user['signInUserSession'] || user['session']) as
      | { idToken?: { payload?: Record<string, unknown> } }
      | undefined;
    const payload = session?.idToken?.payload;
    if (payload && 'custom:tenantId' in payload) {
      return payload['custom:tenantId'];
    }
    return undefined;
  }
}
